import { Anchor, ShieldCheck } from "lucide-react";
import { Eyebrow, ArrowCta, Reveal, MaskLineView, CheckItem, Faq } from "../components/ui-bits";
import JsonLd from "../components/JsonLd";
import { useLang } from "../i18n/LangContext";

const HERO_IMG = "/broker-hero.jpg";

const COPY = {
  en: {
    heroEyebrow: "For brokers & advisers",
    heroL1: "Have a developer",
    heroL2: "who needs capital?",
    heroCopy:
      "Bring us the deal. We pay 1% of the funded amount on your introduction — rising to 1.2% and then 1.5% as the relationship grows — and we tell you yes or no within 48 hours.",
    heroAlt: "A broker and a Verde Capital site manager overlooking a coastal development site in Portugal",
    ctaSubmit: "Submit a deal",
    ctaHow: "How we pay brokers",
    feesEyebrow: "What you earn",
    feesH: "We pay you, not the developer.",
    feesCopy:
      "1% of the funded amount on your first deal. 1.2% on your second. 1.5% from your third funded deal onwards. Paid by Verde — never charged to the developer — within 10 working days of drawdown.",
    calloutKicker: "One introduction",
    calloutPre: "Introduce a developer who borrows €2m four times, and that one introduction is worth",
    calloutFig: "€104,000",
    protEyebrow: "Your introduction is protected",
    protCopy:
      "We register every introduction for 12 months. If that developer takes a loan from us in that time — whether they came through you or not — you are paid. We will not approach a developer you introduced except through you.",
    dealsEyebrow: "Deals Verde Capital wants",
    deals: [
      "Senior facilities of €1–5M for experienced developers",
      "Projects in Portugal — land acquisition, licensing, pre-sales",
      "A clear path to bank financing, sale or stabilisation",
      "Developer equity already invested in the project",
    ],
    whoEyebrow: "Who can introduce deals",
    who: [
      "Brokerages and deal finders",
      "Finance and debt advisers",
      "Lawyers and tax advisers",
      "Real estate agents and consultants",
    ],
    howEyebrow: "How submissions work",
    howH: "Three steps. One relationship.",
    how: [
      { n: "1", t: "Submit the deal", c: "A short online submission — the developer, the project and the financing needed." },
      { n: "2", t: "We underwrite", c: "We underwrite — and you hear from us within 48 hours, either with questions or with a no and the reason." },
      { n: "3", t: "Move it forward", c: "If it fits, we move to indicative terms, due diligence and closing — together." },
    ],
    whyEyebrow: "Why introduce to Verde Capital",
    benefits: [
      { t: "Paid on every deal", c: "Not just the first. 1% on your first deal, 1.2% on your second, 1.5% from your third onwards." },
      { t: "An answer in 48 hours", c: "Including the no, with the reason. You stop wasting weeks on a maybe." },
      { t: "You stay on the file", c: "We tell you where the deal is as it moves. You never have to chase us for an update." },
    ],
    tiersEyebrow: "Become a Verde Partner",
    regKicker: "Registered broker",
    regH: "Open to anyone, after a short form.",
    regItems: ["1% / 1.2% / 1.5%", "12-month protection", "48-hour answers"],
    regCta: "Submit a deal",
    partnerKicker: "Verde Partner",
    partnerH: "From your third funded deal.",
    partnerItems: ["1.5% on funded deals", "Direct line to the credit team, no form", "First call on capacity", "Quarterly pipeline conversation"],
    partnerCta: "Speak with Verde Capital",
    faqEyebrow: "FAQs",
    faqs: [
      { q: "Who can introduce deals to Verde Capital?", a: "Brokers, advisers and real estate professionals introducing qualifying development opportunities in Portugal." },
      { q: "How do I submit a deal?", a: "Through our simple online submission form. It takes a few minutes and you'll receive a reference for tracking." },
      { q: "When and how am I paid?", a: "Within 10 working days of drawdown, by bank transfer, on our invoice approval. We pay you directly — the developer is never charged a broker fee." },
      { q: "What happens if my developer comes back without me?", a: "Your introduction is registered for 12 months. Any loan that developer takes from us in that period pays you commission — 1% on the first, 1.2% on the second, 1.5% thereafter — whether they came through you or not." },
      { q: "Will I stay involved after introducing the deal?", a: "Yes. We keep introducing partners informed as underwriting progresses — you stay part of the deal." },
      { q: "Can I become a Verde Partner?", a: "Yes. Complete two funded loans and you're a repeat partner from your third funded deal onwards — 1.5% on funded deals, a direct line to the credit team and first call on capacity." },
    ],
  },
  pt: {
    heroEyebrow: "Para intermediários e consultores",
    heroL1: "Tem um promotor",
    heroL2: "que precisa de capital?",
    heroCopy:
      "Traga-nos o negócio. Pagamos 1% do montante financiado pela sua apresentação — a subir para 1,2% e depois 1,5% à medida que a relação cresce — e damos-lhe um sim ou um não em 48 horas.",
    heroAlt: "Um intermediário e um gestor de obra da Verde Capital numa empreitada costeira em Portugal",
    ctaSubmit: "Apresentar negócio",
    ctaHow: "Como pagamos os intermediários",
    feesEyebrow: "O que ganha",
    feesH: "Pagamos-lhe a si, não ao promotor.",
    feesCopy:
      "1% do montante financiado no seu primeiro negócio. 1,2% no segundo. 1,5% a partir do terceiro negócio financiado. Pago pela Verde — nunca cobrado ao promotor — no prazo de 10 dias úteis após a disponibilização de fundos.",
    calloutKicker: "Uma apresentação",
    calloutPre: "Apresente um promotor que contrate €2m quatro vezes e essa única apresentação vale",
    calloutFig: "€104.000",
    protEyebrow: "A sua apresentação está protegida",
    protCopy:
      "Registamos todas as apresentações durante 12 meses. Se esse promotor contratar um empréstimo connosco nesse período — tenha vindo por si ou não — a comissão é paga. Não abordaremos um promotor que nos apresentou senão através de si.",
    dealsEyebrow: "Negócios que a Verde Capital procura",
    deals: [
      "Operações séniores de €1–5M para promotores experientes",
      "Projetos em Portugal — aquisição de terreno, licenciamento, pré-vendas",
      "Um caminho claro para o financiamento bancário, venda ou estabilização",
      "Capital próprio do promotor já investido no projeto",
    ],
    whoEyebrow: "Quem pode apresentar negócios",
    who: [
      "Sociedades de intermediação e captadores de negócios",
      "Consultores financeiros e de crédito",
      "Advogados e consultores fiscais",
      "Consultores e agentes imobiliários",
    ],
    howEyebrow: "Como funcionam as apresentações",
    howH: "Três passos. Uma relação.",
    how: [
      { n: "1", t: "Apresente o negócio", c: "Um formulário online breve — o promotor, o projeto e o financiamento necessário." },
      { n: "2", t: "Fazemos a subscrição", c: "Subscrevemos o processo — e terá notícias nossas em 48 horas, seja com perguntas, seja com um não e o motivo." },
      { n: "3", t: "Avançamos juntos", c: "Se encaixar, avançamos para condições indicativas, due diligence e fecho — juntos." },
    ],
    whyEyebrow: "Porque apresentar à Verde Capital",
    benefits: [
      { t: "Pago em todos os negócios", c: "Não apenas no primeiro. 1% no seu primeiro negócio, 1,2% no segundo, 1,5% a partir do terceiro." },
      { t: "Resposta em 48 horas", c: "Incluindo o não, com o motivo. Deixa de perder semanas num talvez." },
      { t: "Mantém-se no processo", c: "Dizemos-lhe onde o negócio está à medida que avança. Nunca precisa de nos perseguir por atualizações." },
    ],
    tiersEyebrow: "Tornar-se Verde Partner",
    regKicker: "Intermediário registado",
    regH: "Aberto a todos, após um formulário breve.",
    regItems: ["1% / 1,2% / 1,5%", "Proteção de 12 meses", "Respostas em 48 horas"],
    regCta: "Apresentar negócio",
    partnerKicker: "Verde Partner",
    partnerH: "A partir do seu terceiro negócio financiado.",
    partnerItems: ["1,5% nos negócios financiados", "Linha direta com a equipa de crédito, sem formulário", "Prioridade na capacidade", "Conversa de pipeline trimestral"],
    partnerCta: "Falar com a Verde Capital",
    faqEyebrow: "Perguntas frequentes",
    faqs: [
      { q: "Quem pode apresentar negócios à Verde Capital?", a: "Intermediários, consultores e profissionais imobiliários que apresentem oportunidades de desenvolvimento elegíveis em Portugal." },
      { q: "Como apresento um negócio?", a: "Através do nosso simples formulário online. Demora alguns minutos e receberá uma referência para acompanhamento." },
      { q: "Quando e como sou pago?", a: "No prazo de 10 dias úteis após a disponibilização de fundos, por transferência bancária, após aprovação da nossa fatura. Pagamos-lhe diretamente — o promotor nunca paga comissão de intermediação." },
      { q: "E se o meu promotor voltar sem mim?", a: "A sua apresentação fica registada durante 12 meses. Qualquer empréstimo que esse promotor contrate de nós nesse período paga-lhe comissão — 1% no primeiro, 1,2% no segundo, 1,5% nos seguintes — tenha vindo por si ou não." },
      { q: "Continuo envolvido depois de apresentar o negócio?", a: "Sim. Mantemos os parceiros apresentadores informados à medida que a subscrição avança — faz parte do negócio." },
      { q: "Posso tornar-me um Verde Partner?", a: "Sim. Complete dois empréstimos financiados e é parceiro recorrente a partir do seu terceiro negócio financiado — 1,5% nos negócios financiados, linha direta com a equipa de crédito e prioridade na capacidade." },
    ],
  },
  nl: {
    heroEyebrow: "Voor tussenpersonen en adviseurs",
    heroL1: "Heeft u een ontwikkelaar",
    heroL2: "die kapitaal nodig heeft?",
    heroCopy:
      "Breng de deal naar ons. Wij betalen 1% van het gefinancierde bedrag voor uw introductie — oplopend naar 1,2% en daarna 1,5% naarmate de relatie groeit — en u krijgt binnen 48 uur een ja of een nee.",
    heroAlt: "Een tussenpersoon en een bouwleider van Verde Capital op een kustbouwproject in Portugal",
    ctaSubmit: "Deal indienen",
    ctaHow: "Zo betalen wij tussenpersonen",
    feesEyebrow: "Wat u verdient",
    feesH: "Wij betalen u, niet de ontwikkelaar.",
    feesCopy:
      "1% van het gefinancierde bedrag bij uw eerste deal. 1,2% bij uw tweede. 1,5% vanaf uw derde gefinancierde deal. Betaald door Verde — nooit in rekening gebracht bij de ontwikkelaar — binnen 10 werkdagen na uitbetaling van de middelen.",
    calloutKicker: "Eén introductie",
    calloutPre: "Brengt u een ontwikkelaar binnen die vier keer €2 mln opneemt, dan is die ene introductie",
    calloutFig: "€104.000",
    protEyebrow: "Uw introductie is beschermd",
    protCopy:
      "Wij registreren alle introducties gedurende 12 maanden. Neemt die ontwikkelaar in die periode een lening bij ons — al dan niet via u — dan wordt de provisie betaald. Een ontwikkelaar die u ons aanbrengt benaderen wij nooit anders dan via u.",
    dealsEyebrow: "Deals waar Verde Capital naar zoekt",
    deals: [
      "Senior transacties van €1–5 mln voor ervaren ontwikkelaars",
      "Projecten in Portugal — grondverwerving, vergunningen, voorverkoop",
      "Een duidelijke route naar bankfinanciering, verkoop of stabilisatie",
      "Eigen kapitaal van de ontwikkelaar al in het project",
    ],
    whoEyebrow: "Wie kan deals indienen",
    who: [
      "Intermediairkantoren en dealmakers",
      "Financiële en kredietadviseurs",
      "Advocaten en belastingadviseurs",
      "Makelaars en vastgoedagenten",
    ],
    howEyebrow: "Hoe introducties werken",
    howH: "Drie stappen. Eén relatie.",
    how: [
      { n: "1", t: "Dien de deal in", c: "Een kort online formulier — de ontwikkelaar, het project en de benodigde financiering." },
      { n: "2", t: "Wij beoordelen", c: "Wij toetsen het dossier — en u hoort binnen 48 uur van ons, met vragen of met een nee en de reden." },
      { n: "3", t: "Samen verder", c: "Past het? Dan gaan we samen door naar indicatieve voorwaarden, due diligence en sluiting." },
    ],
    whyEyebrow: "Waarom introduceren bij Verde Capital",
    benefits: [
      { t: "Betaald bij elke deal", c: "Niet alleen de eerste. 1% bij uw eerste deal, 1,2% bij uw tweede, 1,5% vanaf uw derde." },
      { t: "Antwoord binnen 48 uur", c: "Ook het nee, met de reden. Nooit meer weken wachten op een misschien." },
      { t: "U blijft betrokken", c: "Wij laten u zien waar de deal staat terwijl hij vordert. U hoeft ons nooit achterna te zitten voor updates." },
    ],
    tiersEyebrow: "Verde Partner worden",
    regKicker: "Geregistreerde tussenpersoon",
    regH: "Voor iedereen, na een kort formulier.",
    regItems: ["1% / 1,2% / 1,5%", "12 maanden bescherming", "Antwoord binnen 48 uur"],
    regCta: "Deal indienen",
    partnerKicker: "Verde Partner",
    partnerH: "Vanaf uw derde gefinancierde deal.",
    partnerItems: ["1,5% op gefinancierde deals", "Directe lijn met het kredietteam, zonder formulier", "Prioriteit bij capaciteit", "Kwartaalgesprek over de pijplijn"],
    partnerCta: "Praat met Verde Capital",
    faqEyebrow: "Veelgestelde vragen",
    faqs: [
      { q: "Wie kan deals bij Verde Capital indienen?", a: "Tussenpersonen, adviseurs en vastgoedprofessionals die in aanmerking komende ontwikkelkansen in Portugal aandragen." },
      { q: "Hoe dien ik een deal in?", a: "Via ons korte online formulier. Het kost enkele minuten en u ontvangt een referentie voor opvolging." },
      { q: "Wanneer en hoe word ik betaald?", a: "Binnen 10 werkdagen na uitbetaling van de middelen, per bankoverschrijving, na goedkeuring van onze factuur. Wij betalen u rechtstreeks — de ontwikkelaar betaalt nooit provisie." },
      { q: "Wat als mijn ontwikkelaar zonder mij terugkomt?", a: "Uw introductie staat 12 maanden geregistreerd. Elke lening die die ontwikkelaar in die periode bij ons afsluit, betaalt u provisie — 1% voor de eerste, 1,2% voor de tweede, 1,5% voor volgende — al dan niet via u binnengekomen." },
      { q: "Blijf ik betrokken na de introductie?", a: "Ja. Wij houden introducerende partners op de hoogte zodra de beoordeling vordert — dat hoort bij de deal." },
      { q: "Kan ik Verde Partner worden?", a: "Ja. Sluit twee gefinancierde leningen en u bent vaste partner vanaf uw derde gefinancierde deal — 1,5% op gefinancierde deals, directe lijn met het kredietteam en prioriteit bij capaciteit." },
    ],
  },
};

export default function Brokers() {
  const { lang, lp } = useLang();
  const c = COPY[lang];
  return (
    <div className="bg-cream">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: c.faqs.map((f) => ({
          "@type": "Question", name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }} />
      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="pt-32 sm:pt-40 pb-16 lg:pb-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>{c.heroEyebrow}</Eyebrow>
            <h1 className="display mt-6 text-[2.6rem] sm:text-5xl lg:text-[56px] text-charcoal" data-testid="page-headline">
              <MaskLineView>{c.heroL1}</MaskLineView>
              <MaskLineView delay={0.12}>{c.heroL2}</MaskLineView>
            </h1>
            <Reveal delay={0.25} className="mt-7 max-w-lg">
              <p className="text-lg leading-relaxed text-ink">{c.heroCopy}</p>
            </Reveal>
            <Reveal delay={0.35} className="mt-9 flex flex-wrap gap-3.5">
              <ArrowCta to={lp("/brokers/submit")} testid="brokers-cta">{c.ctaSubmit}</ArrowCta>
              <a
                href="#fees"
                data-testid="brokers-cta-fees"
                className="group inline-flex items-center justify-center gap-2.5 rounded-full border border-sand bg-white px-6 py-3 text-sm font-semibold text-charcoal transition-all duration-300 hover:border-verde-700"
              >
                {c.ctaHow}
                <Anchor className="h-4 w-4" />
              </a>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <img src={HERO_IMG} alt={c.heroAlt} className="h-[340px] sm:h-[440px] w-full object-cover rounded-2xl" data-testid="brokers-hero-image" />
          </Reveal>
        </div>
      </section>

      {/* ── FEES ─────────────────────────────────────────── */}
      <section id="fees" className="py-20 lg:py-28 bg-white border-y border-sand scroll-mt-24">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>{c.feesEyebrow}</Eyebrow>
            <h2 className="display mt-5 text-3xl sm:text-4xl lg:text-5xl text-charcoal" data-testid="fees-heading">
              <MaskLineView>{c.feesH}</MaskLineView>
            </h2>
            <Reveal delay={0.2} className="mt-7 max-w-xl">
              <p className="text-lg text-ink leading-relaxed">{c.feesCopy}</p>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div
              className="relative overflow-hidden rounded-2xl bg-verde-950 p-8 sm:p-10 text-cream grain"
              data-testid="fees-callout"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mint">{c.calloutKicker}</p>
              <p className="mt-6 text-lg leading-relaxed text-cream/85">
                {c.calloutPre}{" "}
                <span className="inline-block align-baseline font-display text-5xl sm:text-6xl font-black tracking-tight text-mint px-1">
                  {c.calloutFig}
                </span>
                .
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── PROTECTION ───────────────────────────────────── */}
      <section className="py-16 lg:py-20 bg-cream-dark/60" data-testid="protection-section">
        <div className="container-x flex flex-col lg:flex-row items-start gap-8 lg:gap-14">
          <div className="shrink-0">
            <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-verde-800 text-mint">
              <ShieldCheck className="h-7 w-7" strokeWidth={1.6} />
            </span>
          </div>
          <div className="max-w-3xl">
            <Eyebrow>{c.protEyebrow}</Eyebrow>
            <Reveal delay={0.15} className="mt-5">
              <p className="text-lg text-ink leading-relaxed">{c.protCopy}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── DEALS & WHO ──────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-white border-b border-sand">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <Eyebrow>{c.dealsEyebrow}</Eyebrow>
            <ul className="mt-8 space-y-4">
              {c.deals.map((d, i) => (
                <Reveal key={d} delay={i * 0.06}>
                  <CheckItem>{d}</CheckItem>
                </Reveal>
              ))}
            </ul>
          </div>
          <div>
            <Eyebrow>{c.whoEyebrow}</Eyebrow>
            <ul className="mt-8 space-y-4">
              {c.who.map((w, i) => (
                <Reveal key={w} delay={i * 0.06}>
                  <CheckItem>{w}</CheckItem>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── HOW SUBMISSIONS WORK ─────────────────────────── */}
      <section className="py-20 lg:py-28">
        <div className="container-x">
          <Eyebrow>{c.howEyebrow}</Eyebrow>
          <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal max-w-2xl">
            <MaskLineView>{c.howH}</MaskLineView>
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {c.how.map((h, i) => (
              <Reveal key={h.n} delay={0.08 + i * 0.1}>
                <div className="card h-full p-8" data-testid={`how-${h.n}`}>
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-verde-50 font-mono text-xs text-verde-800">{h.n}</span>
                  <h3 className="mt-6 font-display text-lg font-bold text-charcoal">{h.t}</h3>
                  <p className="mt-2 text-sm text-ink leading-relaxed">{h.c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY INTRODUCE ────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-cream-dark/60">
        <div className="container-x">
          <Eyebrow>{c.whyEyebrow}</Eyebrow>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {c.benefits.map((b, i) => (
              <Reveal key={b.t} delay={0.08 + i * 0.1}>
                <div className="card h-full p-8" data-testid={`benefit-${i}`}>
                  <h3 className="font-display text-lg font-bold text-charcoal">{b.t}</h3>
                  <p className="mt-2 text-sm text-ink leading-relaxed">{b.c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── TWO TIERS ────────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-white border-y border-sand">
        <div className="container-x">
          <Eyebrow>{c.tiersEyebrow}</Eyebrow>
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-5 items-stretch">
            <Reveal>
              <div className="card h-full p-8 lg:p-10 flex flex-col" data-testid="tier-registered">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-verde-700">{c.regKicker}</p>
                <h3 className="mt-4 font-display text-2xl font-bold text-charcoal">{c.regH}</h3>
                <ul className="mt-6 space-y-3 flex-1">
                  {c.regItems.map((s) => (
                    <li key={s} className="flex items-center gap-3 text-sm text-ink">
                      <span className="h-1.5 w-1.5 rotate-45 bg-brass" /> {s}
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <ArrowCta to={lp("/brokers/submit")} variant="ghost" testid="tier-registered-cta">{c.regCta}</ArrowCta>
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.12}>
              <div className="relative h-full rounded-2xl bg-verde-950 text-cream p-8 lg:p-10 flex flex-col grain overflow-hidden" data-testid="tier-partner">
                <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-mint">{c.partnerKicker}</p>
                <h3 className="mt-4 font-display text-2xl font-bold text-white">{c.partnerH}</h3>
                <ul className="mt-6 space-y-3 flex-1">
                  {c.partnerItems.map((s) => (
                    <li key={s} className="flex items-start gap-3 text-sm text-cream/85">
                      <span className="mt-1.5 h-1.5 w-1.5 rotate-45 bg-mint shrink-0" /> {s}
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <ArrowCta to={lp("/contact")} variant="light" testid="tier-partner-cta">{c.partnerCta}</ArrowCta>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── FAQS ─────────────────────────────────────────── */}
      <section className="py-20 lg:py-28">
        <div className="container-x max-w-4xl">
          <Eyebrow>{c.faqEyebrow}</Eyebrow>
          <Reveal delay={0.15} className="mt-10">
            <Faq items={c.faqs} />
          </Reveal>
          <Reveal delay={0.25} className="mt-10">
            <ArrowCta to={lp("/brokers/submit")} testid="brokers-bottom-cta">{c.ctaSubmit}</ArrowCta>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
